/**
 * AI 응답 파싱 유틸리티
 */

import type { AIResponseWithReason } from '../types'

export interface ParseOptions {
  fields: {
    primary: string | string[]  // e.g., 'improved' or ['improved', 'paraphrased']
    reason?: string
  }
  defaultReason: string
}

/**
 * AI 응답에서 JSON을 파싱하고 처리
 */
export function parseAIResponse(
  response: string, 
  options: ParseOptions
): AIResponseWithReason {
  // 먼저 완전한 JSON인지 확인
  if (response.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(response)
      return extractFromParsed(parsed, options)
    } catch (e) {
      // JSON 형태지만 파싱 실패 - 불완전한 JSON일 가능성
      // improved 또는 text 필드를 직접 추출 시도
      const improvedMatch = response.match(/"improved"\s*:\s*"([^"]*(?:\\.[^"]*)*)"/)
      const textMatch = response.match(/"text"\s*:\s*"([^"]*(?:\\.[^"]*)*)"/)
      const reasonMatch = response.match(/"reason"\s*:\s*"([^"]*(?:\\.[^"]*)*)"/)
      
      if (improvedMatch || textMatch) {
        let text = improvedMatch ? improvedMatch[1] : textMatch![1]
        const reason = reasonMatch ? reasonMatch[1] : options.defaultReason
        
        // JSON 문자열 디코딩 (이스케이프 문자 처리)
        text = text
          .replace(/\\"/g, '"')
          .replace(/\\\\/g, '\\')
          .replace(/\\n/g, '\n')
          .replace(/\\r/g, '\r')
          .replace(/\\t/g, '\t')
          .replace(/\\b/g, '\b')
          .replace(/\\f/g, '\f')
        
        return { text, reason }
      }
    }
  }
  
  // JSON이 아니거나 추출 실패 시 기존 방법 시도
  try {
    // 응답에서 JSON 추출 시도 (정규식으로 안전하게)
    const jsonMatch = response.match(/\{[\s\S]*\}/)
    if (jsonMatch) {
      try {
        const parsed = JSON.parse(jsonMatch[0])
        return extractFromParsed(parsed, options)
      } catch {}
    }
  } catch {}
  
  // 텍스트 형식 파싱 시도 (개선된 텍스트:, 확장된 텍스트:, 축소된 텍스트: 등)
  const primaryFields = Array.isArray(options.fields.primary) 
    ? options.fields.primary 
    : [options.fields.primary]
  
  for (const field of primaryFields) {
    // Korean labels mapping
    const fieldLabels: Record<string, string> = {
      'improved': '개선된 텍스트',
      'paraphrased': '개선된 텍스트',
      'expanded': '확장된 텍스트', 
      'shortened': '축소된 텍스트',
      'continuation': '이어쓴 텍스트'
    }
    
    const label = fieldLabels[field] || field
    const textPattern = new RegExp(`${label}[:\\s]+\\[?([^\\]]+)\\]?`, 'im')
    const textMatch = response.match(textPattern)
    
    if (textMatch) {
      let text = textMatch[1].trim()
      
      // 변경 이유 찾기 및 제거
      const reasonPattern = /변경\s*이유[:\s]+\[?([^\]]+)\]?/im
      const reasonMatch = response.match(reasonPattern)
      const reason = reasonMatch ? reasonMatch[1].trim() : options.defaultReason
      
      // 텍스트에서 변경 이유가 포함된 경우 제거
      if (text.includes('변경 이유') || text.includes('변경이유')) {
        text = text.replace(/변경\s*이유[:\s]+.*/gi, '').trim()
      }
      
      return { text, reason }
    }
  }
  
  // 모든 파싱 시도 실패 시 - 변경 이유를 제거한 텍스트 반환
  console.warn('Failed to parse AI response:', response.substring(0, 200))
  
  // 변경 이유 관련 텍스트 제거
  let cleanedResponse = response
  
  // 변경 이유 관련 패턴 찾기
  const reasonPattern = /변경\s*이유[:\s]+(.+?)(?=\n\n|$)/sim
  const reasonMatch = cleanedResponse.match(reasonPattern)
  let extractedReason = options.defaultReason
  
  if (reasonMatch) {
    // 변경 이유 추출
    extractedReason = reasonMatch[1].trim()
    // 변경 이유 부분 제거
    cleanedResponse = cleanedResponse.replace(reasonPattern, '').trim()
  }
  
  // 기타 변경 이유 관련 텍스트 제거
  const patternsToRemove = [
    /^\[?변경\s*이유[:\s]+.*?\]?$/gim,
    /^이유[:\s]+.*$/gim,
    /^reason[:\s]+.*$/gim
  ]
  
  for (const pattern of patternsToRemove) {
    cleanedResponse = cleanedResponse.replace(pattern, '')
  }
  
  return { 
    text: cleanedResponse.trim(), 
    reason: extractedReason 
  }
}

/**
 * 파싱된 객체에서 필드 추출
 */
function extractFromParsed(
  parsed: any, 
  options: ParseOptions
): AIResponseWithReason {
  const primaryFields = Array.isArray(options.fields.primary) 
    ? options.fields.primary 
    : [options.fields.primary]
  
  // primary 필드 찾기
  let text = ''
  for (const field of primaryFields) {
    if (parsed[field]) {
      text = parsed[field]
      break
    }
  }
  
  if (!text) {
    throw new Error('No valid text field found')
  }
  
  // reason 필드 추출
  const reasonField = options.fields.reason || 'reason'
  const reason = parsed[reasonField] || options.defaultReason
  
  return { text, reason }
}

/**
 * Continue 응답 처리 (줄바꿈 정리 포함)
 */
export function parseContinueResponse(
  response: string,
  defaultReason: string
): AIResponseWithReason | string {
  try {
    const parsed = JSON.parse(response)
    const text = (parsed.continuation || parsed.text || '').replace(/^\n+|\n+$/g, '').replace(/\n{3,}/g, '\n\n')
    // 이어쓰기는 생성이므로 reason을 반환하지 않음
    return {
      text,
      reason: undefined
    }
  } catch (e) {
    // JSON 파싱 실패 시 응답에서 JSON 추출 시도
    const jsonMatch = response.match(/\{[\s\S]*\}/)
    if (jsonMatch) {
      try {
        const parsed = JSON.parse(jsonMatch[0])
        if (parsed.continuation || parsed.text) {
          const text = (parsed.continuation || parsed.text || '').replace(/^\n+|\n+$/g, '').replace(/\n{3,}/g, '\n\n')
          return {
            text,
            reason: undefined
          }
        }
      } catch {}
    }
    
    // 텍스트 형식 파싱 시도 - "이어쓴 텍스트:" 패턴
    const textPattern = /이어쓴 텍스트[:\s]+(.+)$/sim
    const textMatch = response.match(textPattern)
    
    if (textMatch) {
      const text = textMatch[1].trim().replace(/^\n+|\n+$/g, '').replace(/\n{3,}/g, '\n\n')
      return { text, reason: undefined }
    }
    
    // 모든 파싱 시도 실패 시 원본 반환 (줄바꿈 정리)
    // 하지만 변경 이유 관련 텍스트는 제거
    let cleanedResponse = response
    
    // 변경 이유 관련 텍스트 제거 패턴들
    const patternsToRemove = [
      /변경\s*이유[:\s]+.*/gim,
      /reason[:\s]+.*/gim,
      /이유[:\s]+.*/gim,
      /설명[:\s]+.*/gim,
      /^[-*]\s*변경.*/gim,
      /^\[변경.*/gim,
      /^이어쓴\s*텍스트[:\s]*/gim
    ]
    
    for (const pattern of patternsToRemove) {
      cleanedResponse = cleanedResponse.replace(pattern, '')
    }
    
    return cleanedResponse.replace(/^\n+|\n+$/g, '').replace(/\n{3,}/g, '\n\n').trim()
  }
}

/**
 * 일반 텍스트 응답 처리
 */
export function parseTextResponse(response: string): string {
  return response.trim()
}